import React, { useContext, useState } from 'react';
import {
  Center,
  Box,
  Heading,
  Text,
  Icon,
  IconName,
  GridItem,
  Grid,
  GridItemProps,
  useDisclosure,
} from '@televet/kibble-ui';
import { ReactComponent as TemplateIllustration } from './form-template.svg';
import * as templateFiles from './templates';
import useForms from 'pages/Forms/hooks/useForms';
import { FormsContext } from 'pages/Forms/context/FormsContext';
import sortBy from 'lodash-es/sortBy';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import { ClinicFormTemplateFragment } from 'shared/types/graphql';
import UploadFormsModal from './UploadFormsModal';
import { useTracking } from 'shared/providers/TrackingProvider';
import { FormsEvents } from 'shared/providers/TrackingProvider/EventNames';

type FormTemplateContent = ClinicFormTemplateFragment['content'];

interface IconCardProps {
  name: IconName;
  onClick: () => void;
  children: React.ReactNode;
}

interface TemplateCardProps {
  onClick: () => void;
  template: FormTemplateContent;
}

const TemplateSelector = (): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const templates = sortBy(Object.values(templateFiles), 'formName');
  const { addForm } = useForms();
  const { dispatch } = useContext(FormsContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { trackEvent } = useTracking();

  const handleSelectTemplate = async (title?: string, content?: unknown): Promise<void> => {
    setLoading(true);
    await addForm(title, content);
    dispatch({
      type: 'ADD_NEW_FORM',
      payload: {},
    });
    setLoading(false);
  };

  const handleUpload = (): void => {
    trackEvent(FormsEvents.UploadForms);
    onOpen();
  };

  const GridItemCard = ({ children, ...rest }: GridItemProps): JSX.Element => (
    <GridItem flexDir="column" flex={1} mt={3} role="group" cursor="pointer" {...rest}>
      {children}
    </GridItem>
  );

  const IconCard = ({ name, onClick, children }: IconCardProps): JSX.Element => (
    <GridItemCard _hover={{ color: 'text.info' }} onClick={onClick}>
      <Center
        h="120px"
        borderRadius={8}
        borderWidth={1}
        borderStyle="dashed"
        borderColor="border.default"
        bgColor="background.default"
        _groupHover={{
          borderColor: 'border.focus',
          backgroundColor: 'background.primaryAlpha.hover',
        }}
        alignSelf="stretch"
        my={2}
      >
        <Icon name={name} color="currentColor" />
      </Center>
      <Text>{children}</Text>
    </GridItemCard>
  );

  const TemplateCard = ({ onClick, template }: TemplateCardProps): JSX.Element => (
    <GridItemCard key={template.title} fill="text.subtle" cursor="pointer" onClick={onClick}>
      <Center
        h="120px"
        my={2}
        borderRadius="8px"
        bgColor="background.subtle"
        _groupHover={{
          borderColor: 'border.info',
          borderWidth: '1px',
          backgroundColor: 'background.primaryAlpha.hover',
          fill: 'text.info',
        }}
        alignSelf="stretch"
      >
        <TemplateIllustration fill="fillColor" />
      </Center>
      <Text>{template.formName}</Text>
    </GridItemCard>
  );

  return (
    <>
      <Center
        w="100%"
        h="100%"
        bg="background.default"
        scrollBehavior="smooth"
        overflowX="hidden"
        overflowY="auto"
        px={5}
      >
        {loading ? (
          <LoadingSpinner message="Creating form..." size="xl" />
        ) : (
          <Box w="100%" maxW="600px" alignSelf="flex-start" my="60px">
            <Heading as="h1">Add New Form</Heading>
            <Grid templateColumns="repeat(auto-fill, minmax(192px, 1fr))" gap={3}>
              <IconCard
                name="plus"
                onClick={(): Promise<void> => {
                  trackEvent(FormsEvents.AddBlankForm);
                  return handleSelectTemplate();
                }}
              >
                Blank
              </IconCard>
              <IconCard name="upload" onClick={handleUpload}>
                Upload
              </IconCard>
            </Grid>
            <Heading as="h2" size="md" mt={8}>
              Templates
            </Heading>
            <Grid templateColumns="repeat(auto-fill, minmax(192px, 1fr))" gap={3}>
              {templates.map((template, index) => (
                <TemplateCard
                  key={index}
                  template={template}
                  onClick={(): Promise<void> => {
                    trackEvent(FormsEvents.AddTemplateForm, { templateName: template.formName });
                    return handleSelectTemplate(template.formName, template);
                  }}
                />
              ))}
            </Grid>
          </Box>
        )}
      </Center>
      <UploadFormsModal isOpen={isOpen} onClose={onClose} />
    </>
  );
};

export default TemplateSelector;
